import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
} from "./ui/dialog";
import QuantityStepper from "./QuantityStepper";
import {
  BedDouble,
  Bath,
  Flame,
  Coffee,
  Trees,
  Plug,
  Caravan,
  Sofa,
  Users,
  Check,
  Lock,
  Plus,
} from "lucide-react";

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    n
  );

const iconFor = (label) => {
  const l = label.toLowerCase();
  if (l.includes("bed") || l.includes("double") || l.includes("twin") || l.includes("single"))
    return BedDouble;
  if (l.includes("bath") || l.includes("suite") || l.includes("shower") || l.includes("washroom"))
    return Bath;
  if (l.includes("wellness") || l.includes("sauna") || l.includes("spa") || l.includes("fire") || l.includes("log"))
    return Flame;
  if (l.includes("breakfast") || l.includes("coffee")) return Coffee;
  if (l.includes("garden") || l.includes("wood") || l.includes("acre") || l.includes("ground"))
    return Trees;
  if (l.includes("electric") || l.includes("hook")) return Plug;
  if (l.includes("van") || l.includes("caravan") || l.includes("pitch")) return Caravan;
  if (l.includes("living") || l.includes("kitchen") || l.includes("lounge")) return Sofa;
  if (l.includes("sleeps") || l.includes("group")) return Users;
  return Check;
};

const AccommodationModal = ({ item, open, onOpenChange, qty, onChange, disabled, onViewBasket }) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
  }, [item]);

  if (!item) return null;
  const gallery = item.gallery || [item.image];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#f2e5de] border-[#d9c3a8] max-w-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Gallery */}
        <div>
          <div className="aspect-[16/9] w-full overflow-hidden bg-black">
            <img
              src={gallery[active]}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 p-3 bg-[#e9dec6]">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`w-20 h-14 rounded overflow-hidden border-2 transition-colors ${
                    i === active
                      ? "border-[#9b3d0a]"
                      : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="feral-serif text-[#9b3d0a] text-2xl md:text-3xl leading-tight">
                {item.name}
              </h3>
              <div className="flex items-center gap-3 mt-2 text-sm text-[#6e5646]">
                {item.sleeps && (
                  <span className="inline-flex items-center gap-1.5 bg-white border border-[#d9c3a8] px-3 py-1">
                    <Users size={14} className="text-[#ebc754]" />
                    {item.sleeps}
                  </span>
                )}
                <span>{item.nights}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="feral-serif text-[#9b3d0a] text-2xl leading-none">
                {gbp(item.price)}
              </p>
              <p className="text-[#9a876f] text-xs mt-1">/ {item.unit}</p>
            </div>
          </div>

          <p className="text-[#1e231f] leading-relaxed mt-5">{item.longDesc || item.desc || item.blurb}</p>

          {item.features && item.features.length > 0 && (
            <div className="mt-6">
              <h4 className="text-[#9a876f] tracking-[0.14em] text-xs font-bold uppercase mb-4">
                What's included
              </h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {item.features.map((f) => {
                  const Icon = iconFor(f);
                  return (
                    <li key={f} className="flex items-start gap-3">
                      <span className="w-8 h-8 shrink-0 rounded-full bg-[#f0dbbe] text-[#ebc754] flex items-center justify-center">
                        <Icon size={16} />
                      </span>
                      <span className="text-[#2e322c] text-sm leading-snug pt-1.5">
                        {f}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Action */}
          <div className="mt-8 border-t border-[#d9c3a8] pt-6">
            {item.soldOut ? (
              <span className="inline-block bg-[#d9c3a8] text-[#9a876f] text-sm font-bold tracking-wide uppercase px-4 py-2.5 rounded">
                Sold out
              </span>
            ) : disabled ? (
              <div className="flex items-start gap-3 bg-[#f0dbbe] border border-[#d9c3a8] rounded-md p-4">
                <Lock size={18} className="text-[#ebc754] mt-0.5 shrink-0" />
                <p className="text-[#7e3208] text-sm">
                  Add a{" "}
                  <span className="font-semibold">weekend ticket</span> to your
                  order to book this stay.
                </p>
              </div>
            ) : qty > 0 ? (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white/70 border border-[#d9c3a8] rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 shrink-0 rounded-full bg-[#e6f0e2] text-[#55734f] flex items-center justify-center">
                      <Check size={17} />
                    </span>
                    <div>
                      <p className="text-[#1e231f] font-semibold">Added to basket</p>
                      <p className="text-[#7a6a58] text-sm mt-0.5">
                        {qty} {qty === 1 ? "item" : "items"} · {gbp(item.price * qty)}
                      </p>
                    </div>
                  </div>
                  <QuantityStepper value={qty} onChange={onChange} max={typeof item.remaining === "number" ? item.remaining : undefined} />
                </div>
                {onViewBasket && (
                  <button
                    type="button"
                    onClick={onViewBasket}
                    className="w-full border border-[#9b3d0a] text-[#9b3d0a] hover:bg-[#9b3d0a] hover:text-[#f2e5de] py-3 rounded-full tracking-[0.12em] text-sm font-semibold transition-colors"
                  >
                    VIEW BASKET
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={() => onChange(1)}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] py-3.5 rounded-full tracking-[0.15em] text-sm font-semibold transition-colors duration-300"
              >
                <Plus size={18} /> ADD TO ORDER
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccommodationModal;
